import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { workspace } from '../theme';
import { SpotRect, TOUR_HEADER_OFFSET } from './useTargetRect';
import { TourPlacement } from './tourSteps';

const WIDTH = 360;
const GAP = 12;
// Side placements sit against wide panels, so they need more breathing room than a stacked card.
const SIDE_GAP = 40;
// Dropping side cards below the target's centre gives a panel-then-explanation reading order.
const SIDE_DROP = 40;
const MARGIN = 16;
const ACCENT = '#2450C6';

type Position = { top: number; left: number; width: number };

type Props = {
  rect: SpotRect | null;
  padding: number;
  placement: TourPlacement;
  title: string;
  body: string;
  stepNumber: number;
  total: number;
  loading: boolean;
  isFirst: boolean;
  isLast: boolean;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  onOpenWorkflow: () => void;
  zIndex: number;
};

const overlaps = (position: Position, height: number, clear: SpotRect) =>
  position.left < clear.left + clear.width && position.left + position.width > clear.left
  && position.top < clear.top + clear.height && position.top + height > clear.top;

const fits = (position: Position, height: number) =>
  position.top >= MARGIN && position.top + height <= window.innerHeight - MARGIN
  && position.left >= MARGIN && position.left + position.width <= window.innerWidth - MARGIN;

const candidateFor = (side: TourPlacement, clear: SpotRect, height: number, width: number): Position => {
  const centreY = clear.top + clear.height / 2 - height / 2;
  const centreX = clear.left + clear.width / 2 - width / 2;
  switch (side) {
    case 'top': return { top: clear.top - GAP - height, left: centreX, width };
    case 'left': return { top: centreY + SIDE_DROP, left: clear.left - SIDE_GAP - width, width };
    case 'right': return { top: centreY + SIDE_DROP, left: clear.left + clear.width + SIDE_GAP, width };
    case 'bottom-start': return { top: clear.top + clear.height + GAP, left: clear.left, width };
    default: return { top: clear.top + clear.height + GAP, left: centreX, width };
  }
};

export default function TourPopover(props: Props) {
  const { rect, padding, placement, title, body, stepNumber, total, loading, isFirst, isLast } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const height = node.offsetHeight;
    const viewportWidth = window.innerWidth;
    const compact = viewportWidth < 600;
    const width = compact ? viewportWidth - MARGIN * 2 : Math.min(WIDTH, viewportWidth - MARGIN * 2);

    if (!rect) {
      setPosition({ top: Math.max(MARGIN + TOUR_HEADER_OFFSET, window.innerHeight / 2 - height / 2), left: (viewportWidth - width) / 2, width });
      return;
    }
    const clear: SpotRect = {
      top: Math.max(0, rect.top - padding),
      left: Math.max(0, rect.left - padding),
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
    const dock = (): Position => {
      const belowSpace = window.innerHeight - (clear.top + clear.height);
      const useBottom = belowSpace >= height + GAP + MARGIN || clear.top < window.innerHeight / 2;
      return {
        top: useBottom ? window.innerHeight - MARGIN - height : MARGIN + TOUR_HEADER_OFFSET,
        left: MARGIN,
        width,
      };
    };
    if (compact) { setPosition(dock()); return; }

    const opposite: Record<TourPlacement, TourPlacement> = {
      top: 'bottom', bottom: 'top', left: 'right', right: 'left', 'bottom-start': 'top',
    };
    const order: TourPlacement[] = [placement, opposite[placement], 'right', 'left', 'bottom', 'top'];
    const seen = new Set<TourPlacement>();
    for (const side of order) {
      if (seen.has(side)) continue;
      seen.add(side);
      const candidate = candidateFor(side, clear, height, width);
      candidate.left = Math.min(Math.max(candidate.left, MARGIN), window.innerWidth - width - MARGIN);
      if (side === 'left' || side === 'right') {
        candidate.top = Math.min(Math.max(candidate.top, MARGIN), window.innerHeight - height - MARGIN);
      }
      if (fits(candidate, height) && !overlaps(candidate, height, clear)) { setPosition(candidate); return; }
    }
    setPosition(dock());
  }, [rect, padding, placement, title, body, loading, isLast]);

  useLayoutEffect(() => { containerRef.current?.focus(); }, [stepNumber]);

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      const focusable = containerRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
      if (!focusable || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;
      if (event.shiftKey && (activeElement === first || activeElement === containerRef.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && activeElement === last) { event.preventDefault(); first.focus(); }
      return;
    }
    if (loading) return;
    if (event.key === 'ArrowRight') { event.preventDefault(); props.onNext(); }
    if (event.key === 'ArrowLeft' && !isFirst) { event.preventDefault(); props.onBack(); }
  }, [isFirst, loading, props]);

  return (
    <Box
      ref={containerRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="product-tour-title"
      aria-describedby="product-tour-body"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      sx={{
        position: 'fixed', zIndex: props.zIndex, outline: 'none',
        transition: 'top 160ms ease-out, left 160ms ease-out, opacity 160ms ease-out',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        width: position?.width ?? WIDTH,
        opacity: position ? 1 : 0,
      }}
    >
      <Paper sx={{ overflow: 'hidden', borderRadius: `${workspace.radius}px`, boxShadow: workspace.raisedShadow }}>
        <Box sx={{ height: 3, bgcolor: ACCENT }} />
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography id="product-tour-title" variant="subtitle1" fontWeight={700}>{title}</Typography>
          <Typography id="product-tour-body" variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.65 }}>
            {loading ? 'Loading this area\u2026' : body}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 2 }}>
            {Array.from({ length: total }).map((_, index) => (
              <Box key={index} sx={{ height: 4, flexGrow: 1, borderRadius: 2, bgcolor: index < stepNumber ? ACCENT : alpha(ACCENT, 0.15) }} />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Step {stepNumber} of {total}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
            <Button size="small" onClick={props.onSkip} sx={{ color: 'text.secondary' }}>Skip tour</Button>
            <Stack direction="row" spacing={1}>
              {!isFirst && <Button size="small" onClick={props.onBack} disabled={loading}>Back</Button>}
              {isLast
                ? <Button size="small" variant="contained" onClick={props.onOpenWorkflow} disabled={loading}>Start consent workflow</Button>
                : <Button size="small" variant="contained" onClick={props.onNext} disabled={loading}>Next</Button>}
            </Stack>
          </Stack>
          {isLast && (
            <Button size="small" fullWidth onClick={props.onNext} disabled={loading} sx={{ mt: 0.5 }}>Finish</Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
